import dayjs, { dateTimeDisplayFormat } from '~libs/dayjs';

import { faEdit } from '~components/icon';
import { Column } from '~components/table-view';
import { linkCellRender } from '~components/table-view/columnRenderers';

import StatusBadge from '~support-at-home/forms/supplier-invoices/StatusBadge';

import ValidationStatus from './ValidationStatus';

const columnsFactory = (openEdit: (id: string) => void, validateClaim: (id: string) => void) => {
  const columns: Column[] = [
    {
      id: 'reference',
      header: 'Reference',
      type: 'text',
      classNames: {
        header: 'cell-align-left',
        column: 'cell-align-left',
      },
      renderReadOnly: linkCellRender((row) => openEdit(row.id), faEdit, {
        classNames: {
          cell: 'px-1',
        },
      } as any),
    },
    {
      id: 'status_id',
      header: 'Status',
      type: 'custom',
      // The row already carries `status.colour` from lookup_sah_claim_statuses; the previous plain-
      // text render threw it away. Same badge component the supplier-invoices and co-contributions
      // tables use, so the claims list scans the same way as the rest of the feature (TAV-3234 AC4).
      component: StatusBadge,
      classNames: {
        header: 'cell-align-center',
        column: 'cell-align-center',
      },
    },
    {
      id: 'start_date',
      header: 'Start Date',
      type: 'date',
      classNames: {
        header: 'cell-align-right',
        column: 'cell-align-right',
      },
    },
    {
      id: 'end_date',
      header: 'End Date',
      type: 'date',
      classNames: {
        header: 'cell-align-right',
        column: 'cell-align-right',
      },
    },
    {
      id: 'last_modified',
      header: 'Last Updated',
      type: 'text',
      classNames: {
        header: 'cell-align-right',
        column: 'cell-align-right',
      },
      deriveValue: (row) => {
        const timestamp = row.updated_at ?? row.created_at;
        return timestamp ? dayjs(timestamp).format(dateTimeDisplayFormat) : '';
      },
    },
    {
      id: 'claim_count',
      header: 'Claimed',
      type: 'text',
      classNames: {
        header: 'cell-align-right',
        column: 'cell-align-right',
      },
      deriveValue: (row) => row.items_count ?? 0,
    },
    {
      id: 'error_count',
      header: 'Errors',
      type: 'text',
      classNames: {
        header: 'cell-align-right',
        column: 'cell-align-right',
      },
      deriveValue: (row) => row.items_with_errors ?? 0,
    },
    {
      id: 'validated',
      header: 'Validated',
      type: 'checkbox',
      classNames: {
        header: 'cell-align-center',
        column: 'cell-align-center',
      },
      deriveValue: (row) => {
        return row.items_with_errors === 0 && (row.items_count ?? 0) > 0 && !row.is_validating && !!row.validated_at;
      },
    },
    {
      id: 'validated_at',
      header: 'Last Validated',
      type: 'custom',
      style: { width: '200px' },
      classNames: {
        header: 'cell-align-right',
      },
      component: ValidationStatus,
      componentProps: { validateClaim },
    },
  ];

  return columns;
};

export default columnsFactory;
